import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, X, ArrowLeft, CreditCard as CardIcon,
  AlertTriangle, CheckCircle2, ChevronRight, Wallet, CalendarClock, Percent,
} from "lucide-react";

const COLORS = ["#0A0A0A", "#059669", "#E11D48", "#F59E0B", "#2563EB", "#7C3AED", "#DB2777"];

const STATUS_LABEL = { pending: "Bekliyor", partial: "Kısmi", paid: "Ödendi", overdue: "Gecikti" };
const STATUS_COLOR = {
  pending: "text-neutral-700 border-neutral-300",
  partial: "text-amber-700 border-amber-300 bg-amber-50",
  paid: "text-emerald-700 border-emerald-300 bg-emerald-50",
  overdue: "text-rose-700 border-rose-300 bg-rose-50",
};

export default function CreditCards() {
  const [cards, setCards] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    const [{ data: cs }, { data: ac }] = await Promise.all([
      api.get("/credit-cards"),
      api.get("/accounts"),
    ]);
    setCards(cs); setAccounts(ac);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Bu kart ve tüm ekstre kayıtları silinsin mi? (Bağlı Hesap silinmez.)")) return;
    await api.delete(`/credit-cards/${id}`); toast.success("Silindi"); load();
  };

  if (selectedId) {
    const c = cards.find((x) => x.id === selectedId);
    return <CardDetail card={c} onBack={() => { setSelectedId(null); load(); }} />;
  }

  const totalDebt = cards.reduce((s, c) => s + (c.open_debt || 0), 0);
  const totalLimit = cards.reduce((s, c) => s + (c.credit_limit || 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Kart & Ekstre Takibi</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Kredi Kartları</h1>
        </div>
        <button
          data-testid="card-add-button"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2"
        >
          <Plus size={16} /> Yeni Kart
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Kart Sayısı" plainValue={`${cards.length}`} tid="stat-card-count" />
        <Stat label="Toplam Açık Borç" value={totalDebt} tone="rose" tid="stat-card-debt" />
        <Stat label="Toplam Limit" value={totalLimit} tid="stat-card-limit" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.length === 0 && (
          <div className="brutal-card p-10 text-center col-span-full text-neutral-500 text-sm">
            Henüz kart eklenmedi. "Yeni Kart" ile başlayın.
          </div>
        )}
        {cards.map((c) => (
          <div
            key={c.id}
            role="button"
            tabIndex={0}
            data-testid={`card-item-${c.id}`}
            onClick={() => setSelectedId(c.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedId(c.id); } }}
            className="brutal-card text-left cursor-pointer overflow-hidden focus:outline-none focus:ring-2 focus:ring-black"
          >
            {/* Card face */}
            <div className="p-5 text-white" style={{ background: c.color || "#0A0A0A" }}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">{c.bank_name}</div>
                  <div className="font-display text-xl font-bold mt-1">{c.name}</div>
                </div>
                <CardIcon size={22} className="opacity-80" />
              </div>
              <div className="mt-6 font-mono-num text-lg tracking-widest">
                •••• •••• •••• {c.last_four || "----"}
              </div>
              <div className="mt-3 flex items-end justify-between text-[11px] opacity-90">
                <div>
                  <div className="opacity-70">HESAP KESİM</div>
                  <div className="font-mono-num">Her ayın {c.statement_day}'i</div>
                </div>
                <div className="text-right">
                  <div className="opacity-70">SON ÖDEME</div>
                  <div className="font-mono-num">Her ayın {c.due_day}'i</div>
                </div>
              </div>
            </div>
            {/* Card summary */}
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-caps">Açık Borç</div>
                  <div className="font-mono-num text-lg font-semibold text-rose-600 mt-0.5">
                    {fmtTRY(c.open_debt || 0)}
                  </div>
                  {(c.statement_debt > 0 || c.account_debt > 0) && (c.statement_debt > 0 && c.account_debt > 0) && (
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      Ekstre {fmtTRY(c.statement_debt)} · Hesap {fmtTRY(c.account_debt)}
                    </div>
                  )}
                </div>
                <div>
                  <div className="label-caps">Limit</div>
                  <div className="font-mono-num text-lg font-semibold mt-0.5">
                    {fmtTRY(c.credit_limit || 0)}
                  </div>
                  {c.account_id ? (
                    <div className="text-[10px] text-emerald-700 mt-0.5">Bağlı Hesap ✓</div>
                  ) : (
                    <div className="text-[10px] text-amber-600 mt-0.5">Hesap bağlı değil</div>
                  )}
                </div>
              </div>
              {c.credit_limit > 0 && (
                <div>
                  <div className="flex items-center justify-between text-[11px] text-neutral-500 mb-1">
                    <span>Kullanım</span>
                    <span>%{c.utilization_percent || 0}</span>
                  </div>
                  <div className="h-2 bg-neutral-100 border border-black/10">
                    <div
                      className={`h-full ${c.utilization_percent > 80 ? "bg-rose-600" : c.utilization_percent > 50 ? "bg-amber-500" : "bg-black"}`}
                      style={{ width: `${Math.min(100, c.utilization_percent || 0)}%` }}
                    />
                  </div>
                </div>
              )}
              {c.next_statement && (
                <div className="pt-3 border-t border-black/10 flex items-center justify-between text-xs">
                  <span className="text-neutral-500">Sıradaki Vade: {fmtDate(c.next_statement.due_date)}</span>
                  <span className="font-mono-num font-medium">
                    {fmtTRY((c.next_statement.total_debt || 0) - (c.next_statement.paid_amount || 0))}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <span className="label-caps text-neutral-400">{c.open_count || 0} açık ekstre</span>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`card-edit-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); setEditing(c); setOpen(true); }}
                    className="p-1.5 hover:bg-neutral-100"
                  ><Edit3 size={14} /></button>
                  <button
                    data-testid={`card-delete-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                    className="p-1.5 hover:bg-rose-50"
                  ><Trash2 size={14} className="text-rose-600" /></button>
                  <ChevronRight size={16} className="text-neutral-400 mt-1" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <CardModal
          editing={editing}
          accounts={accounts}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, plainValue, tone, tid }) {
  const color = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className="brutal-card p-5" data-testid={tid}>
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num text-2xl md:text-3xl font-semibold mt-3 ${color}`}>
        {plainValue !== undefined ? plainValue : fmtTRY(value)}
      </div>
    </div>
  );
}

function CardModal({ editing, accounts = [], onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    name: "", bank_name: "", last_four: "", credit_limit: 0,
    statement_day: 1, due_day: 10, color: COLORS[0], notes: "",
    account_id: "",
  });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      credit_limit: Number(form.credit_limit) || 0,
      statement_day: Math.max(1, Math.min(31, Number(form.statement_day) || 1)),
      due_day: Math.max(1, Math.min(31, Number(form.due_day) || 10)),
      last_four: (form.last_four || "").slice(-4),
      account_id: form.account_id || null,
    };
    try {
      if (editing) await api.put(`/credit-cards/${editing.id}`, payload);
      else await api.post("/credit-cards", payload);
      toast.success("Kaydedildi"); onSaved();
    } catch { toast.error("Kaydedilemedi"); }
  };

  // Bağlanabilir hesaplar: sadece credit_card tipindeki hesaplar
  const linkableAccounts = (accounts || []).filter((a) => a.account_type === "credit_card");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="card-form-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-black w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Kartı Düzenle" : "Yeni Kart"}</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Kart Adı">
              <input required data-testid="card-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="input" placeholder="Örn: Bonus" />
            </F>
            <F label="Banka">
              <input required data-testid="card-bank" value={form.bank_name}
                     onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                     className="input" placeholder="Örn: Garanti BBVA" />
            </F>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Son 4 Hane">
              <input data-testid="card-last4" maxLength={4} value={form.last_four || ""}
                     onChange={(e) => setForm({ ...form, last_four: e.target.value.replace(/\D/g, "") })}
                     className="input font-mono-num" placeholder="1234" />
            </F>
            <F label="Kart Limiti (₺)">
              <input type="number" step="0.01" data-testid="card-limit" value={form.credit_limit || 0}
                     onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                     className="input font-mono-num" />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Hesap Kesim Günü (1-31)">
              <input type="number" min={1} max={31} data-testid="card-stmt-day"
                     value={form.statement_day}
                     onChange={(e) => setForm({ ...form, statement_day: e.target.value })}
                     className="input font-mono-num" />
            </F>
            <F label="Son Ödeme Günü (1-31)">
              <input type="number" min={1} max={31} data-testid="card-due-day"
                     value={form.due_day}
                     onChange={(e) => setForm({ ...form, due_day: e.target.value })}
                     className="input font-mono-num" />
            </F>
          </div>
          <F label="Renk">
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button key={c} type="button"
                        onClick={() => setForm({ ...form, color: c })}
                        className={`w-9 h-9 border-2 ${form.color === c ? "border-black" : "border-transparent"}`}
                        style={{ background: c }} />
              ))}
            </div>
          </F>
          <F label="Bağlı Hesap (Kredi Kartı tipi)">
            <select data-testid="card-linked-account"
                    value={form.account_id || ""}
                    onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                    className="input bg-white">
              <option value="">{editing ? "— Bağlı hesap yok —" : "— Otomatik oluştur —"}</option>
              {linkableAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} {a.bank_name ? `· ${a.bank_name}` : ""}</option>
              ))}
            </select>
            <div className="text-[11px] text-neutral-500 mt-1">
              {editing
                ? "Bu kartın işlemlerini takip edeceğiniz Hesap. Değiştirebilir veya bağlantıyı kaldırabilirsiniz."
                : "Boş bırakırsanız aynı isimde yeni bir 'Kredi Kartı' tipi Hesap otomatik açılır. Var olan bir Hesabı bağlamak isterseniz seçin."}
            </div>
          </F>
          <F label="Notlar">
            <textarea data-testid="card-notes" value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} className="input" />
          </F>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="card-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}

// =================== Card Detail (Statements) ===================

function CardDetail({ card, onBack }) {
  const [statements, setStatements] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openStmt, setOpenStmt] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [editStmt, setEditStmt] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: st }, { data: ac }] = await Promise.all([
        api.get(`/credit-cards/${card.id}/statements`),
        api.get("/accounts"),
      ]);
      setStatements(st); setAccounts(ac);
    } finally { setLoading(false); }
  }, [card.id]);
  useEffect(() => { load(); }, [load]);

  const remove = async (sid) => {
    if (!window.confirm("Ekstre kaydı silinsin mi? (bağlı ödeme işlemi de silinir)")) return;
    await api.delete(`/card-statements/${sid}`); toast.success("Silindi"); load();
  };

  const openDebt = useMemo(
    () => statements.filter((s) => s.status !== "paid")
                    .reduce((s, x) => s + (x.total_debt - x.paid_amount), 0),
    [statements],
  );
  const totalPaid = useMemo(
    () => statements.reduce((s, x) => s + (x.paid_amount || 0), 0),
    [statements],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <button onClick={onBack} data-testid="card-back-button"
                className="p-2 hover:bg-neutral-100 border border-black/10"><ArrowLeft size={16} /></button>
        <div>
          <p className="label-caps">{card.bank_name} · •••• {card.last_four || "----"}</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{card.name}</h1>
        </div>
        <div className="ml-auto">
          <button
            data-testid="stmt-add-button"
            onClick={() => { setEditStmt(null); setOpenStmt(true); }}
            className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2"
          >
            <Plus size={16} /> Yeni Ekstre
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Stat label="Açık Borç" value={openDebt} tone="rose" tid="stat-detail-debt" />
        <Stat label="Limit" value={card.credit_limit || 0} tid="stat-detail-limit" />
        <Stat label="Kullanım" plainValue={`%${card.utilization_percent || 0}`} tid="stat-detail-util" />
        <Stat label="Bugüne Kadar Ödenen" value={totalPaid} tone="emerald" tid="stat-detail-paid" />
      </div>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="statements-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-4 py-3 label-caps">Dönem</th>
              <th className="text-left px-4 py-3 label-caps">Hesap Kesim</th>
              <th className="text-left px-4 py-3 label-caps">Son Ödeme</th>
              <th className="text-right px-4 py-3 label-caps">Dönem Borcu</th>
              <th className="text-right px-4 py-3 label-caps">Asgari</th>
              <th className="text-right px-4 py-3 label-caps">Ödenen</th>
              <th className="text-right px-4 py-3 label-caps">Kalan</th>
              <th className="text-left px-4 py-3 label-caps">Durum</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-10 text-neutral-500 label-caps">Yükleniyor…</td></tr>
            )}
            {!loading && statements.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-neutral-500">Ekstre yok. Yeni ekleyin.</td></tr>
            )}
            {!loading && statements.map((s) => {
              const remaining = Math.max(0, (s.total_debt || 0) - (s.paid_amount || 0));
              return (
                <tr key={s.id} className="border-b border-black/5 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono-num">{s.period}</td>
                  <td className="px-4 py-3 font-mono-num text-neutral-600">{fmtDate(s.statement_date)}</td>
                  <td className="px-4 py-3 font-mono-num">{fmtDate(s.due_date)}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-rose-600">{fmtTRY(s.total_debt)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-neutral-600">{fmtTRY(s.minimum_payment || 0)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-emerald-700">{fmtTRY(s.paid_amount || 0)}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold">{fmtTRY(remaining)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_COLOR[s.status]}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {s.status !== "paid" && (
                      <button
                        data-testid={`stmt-pay-${s.id}`}
                        onClick={() => setPayingId(s.id)}
                        className="border border-black bg-black text-white px-3 py-1 text-xs uppercase tracking-widest hover:opacity-90 mr-1"
                      >Öde</button>
                    )}
                    <button onClick={() => { setEditStmt(s); setOpenStmt(true); }}
                            className="p-1.5 hover:bg-neutral-100"><Edit3 size={13} /></button>
                    <button data-testid={`stmt-delete-${s.id}`}
                            onClick={() => remove(s.id)}
                            className="p-1.5 hover:bg-rose-50"><Trash2 size={13} className="text-rose-600" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openStmt && (
        <StatementModal
          card={card}
          editing={editStmt}
          onClose={() => setOpenStmt(false)}
          onSaved={() => { setOpenStmt(false); load(); }}
        />
      )}
      {payingId && (
        <PaymentModal
          statement={statements.find((s) => s.id === payingId)}
          accounts={accounts}
          onClose={() => setPayingId(null)}
          onSaved={() => { setPayingId(null); load(); }}
        />
      )}
    </div>
  );
}

function StatementModal({ card, editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    period: "", statement_date: todayISO(), due_date: todayISO(),
    total_debt: 0, minimum_payment: 0, notes: "",
  });
  const [suggesting, setSuggesting] = useState(false);

  const autofillPeriod = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get(`/credit-cards/${card.id}/next-period`);
      setForm((f) => ({ ...f, period: data.period, statement_date: data.statement_date, due_date: data.due_date }));
    } finally { setSuggesting(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      period: form.period,
      statement_date: form.statement_date,
      due_date: form.due_date,
      total_debt: Number(form.total_debt) || 0,
      minimum_payment: Number(form.minimum_payment) || 0,
      notes: form.notes || "",
    };
    if (!payload.period) { toast.error("Dönem seçin (YYYY-MM)"); return; }
    if (payload.total_debt <= 0) { toast.error("Dönem borcu 0'dan büyük olmalı"); return; }
    try {
      if (editing) await api.put(`/card-statements/${editing.id}`, payload);
      else await api.post(`/credit-cards/${card.id}/statements`, payload);
      toast.success("Kaydedildi"); onSaved();
    } catch { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="stmt-form-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-black w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Ekstreyi Düzenle" : "Yeni Ekstre"}</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="Dönem (YYYY-MM)">
              <input required data-testid="stmt-period"
                     value={form.period}
                     placeholder="2026-02"
                     onChange={(e) => setForm({ ...form, period: e.target.value })}
                     className="input font-mono-num" />
            </F>
            {!editing && (
              <div className="flex items-end">
                <button type="button"
                        onClick={autofillPeriod} disabled={suggesting}
                        data-testid="stmt-autofill"
                        className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors disabled:opacity-40 w-full"
                >{suggesting ? "..." : "Otomatik Doldur"}</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Hesap Kesim Tarihi">
              <input required type="date" data-testid="stmt-statement-date"
                     value={form.statement_date}
                     onChange={(e) => setForm({ ...form, statement_date: e.target.value })}
                     className="input" />
            </F>
            <F label="Son Ödeme Tarihi">
              <input required type="date" data-testid="stmt-due-date"
                     value={form.due_date}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     className="input" />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Dönem Borcu (₺)">
              <input required type="number" step="0.01" data-testid="stmt-debt"
                     value={form.total_debt}
                     onChange={(e) => setForm({ ...form, total_debt: e.target.value })}
                     className="input font-mono-num" />
            </F>
            <F label="Asgari Ödeme (₺)">
              <input type="number" step="0.01" data-testid="stmt-min"
                     value={form.minimum_payment}
                     onChange={(e) => setForm({ ...form, minimum_payment: e.target.value })}
                     className="input font-mono-num" />
            </F>
          </div>
          <F label="Notlar">
            <textarea data-testid="stmt-notes"
                      value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} className="input" />
          </F>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="stmt-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function PaymentModal({ statement, accounts, onClose, onSaved }) {
  const remaining = Math.max(0, (statement.total_debt || 0) - (statement.paid_amount || 0));
  const [form, setForm] = useState({
    amount: remaining, account_id: accounts[0]?.id || "", pay_date: todayISO(),
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form.account_id) { toast.error("Ödeme yapılacak hesap seçin"); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Geçerli tutar girin"); return; }
    try {
      await api.post(`/card-statements/${statement.id}/pay`, {
        amount: amt, account_id: form.account_id, pay_date: form.pay_date,
      });
      toast.success("Ödeme kaydedildi"); onSaved();
    } catch { toast.error("Ödeme kaydedilemedi"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="pay-form-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-black w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">Kart Ödemesi</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="brutal-card p-4 bg-neutral-50">
            <div className="flex justify-between text-sm">
              <span className="label-caps">Kalan Borç</span>
              <span className="font-mono-num text-lg font-semibold text-rose-600">{fmtTRY(remaining)}</span>
            </div>
            <div className="flex justify-between text-xs mt-1 text-neutral-500">
              <span>Dönem</span>
              <span className="font-mono-num">{statement.period}</span>
            </div>
          </div>
          <F label="Ödenecek Tutar (₺)">
            <input required type="number" step="0.01" data-testid="pay-amount"
                   value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   className="input font-mono-num text-lg" />
          </F>
          <F label="Ödeme Hesabı">
            <select required data-testid="pay-account"
                    value={form.account_id}
                    onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                    className="input bg-white">
              <option value="">— Seçin —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>
          <F label="Ödeme Tarihi">
            <input required type="date" data-testid="pay-date"
                   value={form.pay_date}
                   onChange={(e) => setForm({ ...form, pay_date: e.target.value })}
                   className="input" />
          </F>
          <div className="text-[11px] text-neutral-500 border-l-2 border-black pl-3">
            Bu ödeme, seçilen hesapta otomatik bir <b>Gider</b> işlemi oluşturur.
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="pay-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Ödemeyi Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}
